import { CalendarDaysSolid } from '@allocab/allocab-icons';
import dayjs from 'dayjs';
import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { twJoin } from 'tailwind-merge';

import { FilterBadge } from '../../components/FilterBadge';
import { TimeScopePicker } from '../../components/time-scope-picker/TimeScopePicker';
import { TimeScope } from '../../components/time-scope-picker/types';
import {
  adjustScopeForConstraints,
  getDefaultTimeScopeValues,
  getTimeScopeForDate,
} from '../../components/time-scope-picker/utils';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { t } from '../../services/translation/translation';
import { formatDate, parseDate } from '../../utils/date';

export const DEPARTURE_AT_FILTER_FORMAT = 'DD-MM-YYYY';
export const DEPARTURE_DAY_SEARCH_PARAMS_KEY = 'departureDay';

type Props = {
  onChange: (_key: string, _value: string | undefined) => void;
};

export const TripDepartureAtFilter = ({ onChange }: Props) => {
  const [searchParams] = useSearchParams();
  const [isVisible, setIsVisible] = useState(false);
  const departureDay = searchParams.get(DEPARTURE_DAY_SEARCH_PARAMS_KEY);
  const departureDayTimestamp = departureDay ? parseDate(departureDay, DEPARTURE_AT_FILTER_FORMAT) : undefined;
  const [scope, setScope] = useState<TimeScope>(
    adjustScopeForConstraints(
      departureDayTimestamp ? getTimeScopeForDate(departureDayTimestamp) : getDefaultTimeScopeValues(),
    ),
  );
  const isActive = !!departureDay;

  const ref = useOnClickOutside(
    useCallback(() => {
      setIsVisible(false);
    }, []),
  );

  const handleChangeScope = useCallback(
    (scope?: TimeScope) => {
      if (scope) {
        const timestamp = parseDate(`${scope.year}-${scope.month + 1}-${scope.day}`, 'YYYY-M-D').valueOf();
        onChange(DEPARTURE_DAY_SEARCH_PARAMS_KEY, dayjs(timestamp).format(DEPARTURE_AT_FILTER_FORMAT));
        setScope(scope);
      } else {
        onChange(DEPARTURE_DAY_SEARCH_PARAMS_KEY, undefined);
        setScope(adjustScopeForConstraints(getDefaultTimeScopeValues()));
      }

      setIsVisible(false);
    },
    [onChange],
  );

  return (
    <div className="relative flex items-center justify-center">
      <FilterBadge onClick={() => setIsVisible(v => !v)} isActive={isActive}>
        <CalendarDaysSolid width={12} height={12} className="fill-current" />
        <span>
          {isActive && departureDay
            ? formatDate(parseDate(departureDay, DEPARTURE_AT_FILTER_FORMAT), t('date.date-longer'))
            : t('common.date')}
        </span>
      </FilterBadge>

      {isVisible ? (
        <div
          ref={ref}
          className={twJoin(
            'absolute top-10 z-10 p-4 bg-white rounded-lg shadow dark:bg-gray-700',
            !isVisible && 'hidden',
          )}
        >
          <TimeScopePicker mode="day" onScopeChange={handleChangeScope} scope={scope} />
        </div>
      ) : null}
    </div>
  );
};
